import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Search, Users, X } from 'lucide-react'

import { BarisProduk } from '@/components/baris-produk'
import { Avatar } from '@/components/ui/avatar'
import { Kolom } from '@/components/layout/kolom'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { useCourses } from '@/features/catalog/api'
import { useExperts } from '@/features/marketplace/api'
import { useServices } from '@/features/services/api'
import { useGroups } from '@/features/sosial/api'
import { formatNumber, formatRupiah } from '@/lib/format'

type Tab = 'orang' | 'kelas' | 'jasa' | 'grup'

/**
 * Satu kotak pencarian untuk semua yang bisa ditemukan di Skava.
 *
 * Tanpa kata kunci, yang tampil adalah orang — seperti Threads, pencarian
 * juga menjadi tempat menemukan siapa yang layak diikuti.
 */
export function CariPage() {
  const [ketikan, setKetikan] = useState('')
  const [kueri, setKueri] = useState('')
  const [tab, setTab] = useState<Tab>('orang')

  // Permintaan dikirim setelah jari berhenti sebentar, bukan setiap huruf.
  useEffect(() => {
    const t = window.setTimeout(() => setKueri(ketikan.trim()), 300)
    return () => window.clearTimeout(t)
  }, [ketikan])

  return (
    <Kolom judul="Cari">
      <div className="pt-2" />

      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4.5 -translate-y-1/2 text-subtle" />
        <input
          type="search"
          value={ketikan}
          onChange={(e) => setKetikan(e.target.value)}
          placeholder="Cari orang, kelas, jasa, atau grup"
          aria-label="Cari orang, kelas, jasa, atau grup"
          autoFocus
          className="h-12 w-full rounded-2xl border border-line bg-page pl-11 pr-11 text-[15px] text-ink outline-none transition placeholder:text-subtle focus:border-line-strong"
        />
        {ketikan ? (
          <button
            type="button"
            onClick={() => setKetikan('')}
            aria-label="Hapus pencarian"
            className="absolute right-3 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full text-subtle hover:bg-line hover:text-ink"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      <Tabs
        variant="garis"
        className="-mx-4 mt-4 md:-mx-6"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'orang', label: 'Orang' },
          { value: 'kelas', label: 'Kelas' },
          { value: 'jasa', label: 'Jasa' },
          { value: 'grup', label: 'Grup' },
        ]}
      />

      {tab === 'orang' ? <HasilOrang kueri={kueri} /> : null}
      {tab === 'kelas' ? <HasilKelas kueri={kueri} /> : null}
      {tab === 'jasa' ? <HasilJasa kueri={kueri} /> : null}
      {tab === 'grup' ? <HasilGrup kueri={kueri} /> : null}
    </Kolom>
  )
}

function HasilOrang({ kueri }: { kueri: string }) {
  const { data, isLoading } = useExperts({ q: kueri || undefined })
  const orang = data?.experts ?? []

  if (isLoading) return <Memuat />
  if (orang.length === 0) return <TidakAda kueri={kueri} />

  return (
    <ul>
      {orang.map((o) => (
        <li key={o.user_id} className="border-b border-line">
          <Link
            to="/app/orang/$userId"
            params={{ userId: o.user_id }}
            className="flex items-center gap-3 py-3 transition hover:opacity-80"
          >
            <Avatar name={o.full_name} src={o.avatar_url || undefined} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{o.full_name}</p>
              <p className="truncate text-sm text-muted">{o.headline || o.skills.slice(0, 3).join(' · ')}</p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function HasilKelas({ kueri }: { kueri: string }) {
  const { data, isLoading } = useCourses({ q: kueri || undefined, sort: 'popular' })
  const kelas = data?.courses ?? []

  if (isLoading) return <Memuat />
  if (kelas.length === 0) return <TidakAda kueri={kueri} />

  return (
    <ul>
      {kelas.map((k) => (
        <BarisProduk
          key={k.id}
          ke="/kelas/$slug"
          slug={k.slug}
          gambar={k.thumbnail_url}
          judul={k.title}
          keterangan={`${k.expert.full_name} · ${formatNumber(k.enrollment_count)} peserta`}
          harga={k.effective_price === 0 ? 'Gratis' : formatRupiah(k.effective_price)}
        />
      ))}
    </ul>
  )
}

function HasilJasa({ kueri }: { kueri: string }) {
  const { data, isLoading } = useServices({ q: kueri || undefined })
  const jasa = data?.services ?? []

  if (isLoading) return <Memuat />
  if (jasa.length === 0) return <TidakAda kueri={kueri} />

  return (
    <ul>
      {jasa.map((j) => (
        <BarisProduk
          key={j.id}
          ke="/jasa/$slug"
          slug={j.slug}
          gambar={j.thumbnail_url}
          judul={j.title}
          keterangan={j.seller.full_name}
          harga={`Mulai ${formatRupiah(j.starting_price)}`}
        />
      ))}
    </ul>
  )
}

function HasilGrup({ kueri }: { kueri: string }) {
  const { data, isLoading } = useGroups(kueri)
  const grup = data?.groups ?? []

  if (isLoading) return <Memuat />
  if (grup.length === 0) return <TidakAda kueri={kueri} />

  return (
    <ul>
      {grup.map((g) => (
        <li key={g.id} className="border-b border-line">
          <Link
            to="/app/grup/$slug"
            params={{ slug: g.slug }}
            className="flex items-center gap-3 py-3 transition hover:opacity-80"
          >
            <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-sunken text-muted">
              {g.cover_url ? (
                <img src={g.cover_url} alt="" className="size-full object-cover" />
              ) : (
                <Users className="size-4.5" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{g.name}</p>
              <p className="truncate text-sm text-muted">
                {formatNumber(g.member_count)} anggota
                {g.privacy === 'private' ? ' · Tertutup' : ''}
              </p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function Memuat() {
  return (
    <div className="space-y-3 pt-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-2xl" />
      ))}
    </div>
  )
}

function TidakAda({ kueri }: { kueri: string }) {
  return (
    <EmptyState
      className="py-12"
      title={kueri ? `Tidak ada hasil untuk "${kueri}"` : 'Belum ada yang bisa ditampilkan'}
      description={kueri ? 'Coba kata lain yang lebih umum.' : undefined}
    />
  )
}
